import { ReactNode, useEffect, useRef, useState } from 'react';
import { Tab, TabGroup, TabList } from '@headlessui/react';
import clsx from 'clsx';

export interface Tab<T extends string = string> {
  label: string;
  value: T;
  count?: number;
  left?: ReactNode;
  right?: ReactNode;
}

interface TabsWithIndicatorProps<T extends string = string> {
  tabs: Tab<T>[];
  activeTab: T;
  onChange: (value: T) => void;
  containerClassName?: string;
  tabClassName?: string;
}

export default function Tabs<T extends string = string>({
  tabs,
  activeTab,
  onChange,
  containerClassName,
  tabClassName,
}: TabsWithIndicatorProps<T>) {
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });
  const tabsRef = useRef<Record<string, HTMLButtonElement | null>>({});

  const selectedIndex = tabs.findIndex((tab) => tab.value === activeTab);

  useEffect(() => {
    const el = tabsRef.current[activeTab];
    if (el) {
      setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [activeTab]);

  return (
    <TabGroup selectedIndex={selectedIndex} onChange={(index) => onChange(tabs[index].value)}>
      <div className={`relative w-full border-b border-gray-300 ${containerClassName ?? ''}`}>
        <TabList className="flex  gap-5 items-center">
          {tabs.map(({ label, value, right, left }) => (
            <Tab
              key={value}
              ref={(el: HTMLButtonElement | null) => (tabsRef.current[value] = el)}
              className={clsx(
                'px-[8px] py-[20px] cursor-pointer relative text-[18px] font-family-outfit leading-[24px] transition-colors duration-200 flex items-center gap-2 outline-none text-primary-900  data-hover:text-primary-500 data-selected:data-hover:text-primary-900',
                tabClassName
              )}
            >
              {left}
              <span>{label}</span>
              {right}
            </Tab>
          ))}
        </TabList>
        <div
          className="absolute bottom-0 h-[4px] bg-[#587EFF] transition-all duration-300 ease-in-out rounded-[4px]"
          style={{ left: indicator.left, width: indicator.width }}
        />
      </div>
    </TabGroup>
  );
}
